import { AllotmentDayStateEnum } from '@koob/enums';
import { decodeId } from 'utils';

export function removeUntouchedValues(values) {
  return values
    ?.filter((r) => r.edited || r.organizations?.some((o) => o.edited))
    ?.map((room) => ({
      ...room,
      organizations: room.organizations
        ?.filter((org) => org.edited)
        ?.map((org) => {
          const allotmentDays = org.allotmentDays
            ?.filter((ad) => ad.edited)
            ?.map((ad) => ({
              ...ad,
              edited: undefined,
            }))
            // Dedicated and shared days of the same date are distinct rows
            .reduce((acc, item) => {
              const key = `${item.day}|${Boolean(item.shared)}`;
              acc[key] = acc[key] || item;
              return acc;
            }, {});
          return {
            ...org,
            allotmentDays: Object.values(allotmentDays),
            edited: undefined,
          };
        }),
      edited: undefined,
    }));
}

export function applyAllotmentEdits({
  rooms,
  selectedRooms,
  selectedOrganizations,
  selectedDays,
  shared,
  state,
  count,
}) {
  const skippedDays = [];

  const newRooms = rooms?.map((room) => {
    if (selectedRooms.some((selectedRoom) => selectedRoom.id === room.id)) {
      const newRoom = {
        ...room,
        edited: true,
      };

      newRoom.organizations = room.organizations.map((org) => {
        if (
          selectedOrganizations.some(
            (selectedOrg) => selectedOrg?.id === org?.id,
          )
        ) {
          const newOrg = {
            ...org,
            edited: true,
          };

          // The grid already holds every sellable day of the organization, so a
          // missing dedicated day means the organization is not sold under any
          // period that day: it is skipped and reported back to the caller.
          const roomSelectedDays = selectedDays.filter(
            (selectedDay) => selectedDay.roomId === room.id,
          );
          const dedicatedDayOf = (day) =>
            org.allotmentDays.find((ad) => ad.day === day && !ad.shared);

          roomSelectedDays
            .filter((selectedDay) => !dedicatedDayOf(selectedDay.day))
            .forEach((selectedDay) =>
              skippedDays.push({
                roomId: room.id,
                organizationId: org.id,
                day: selectedDay.day,
              }),
            );

          // Only a missing shared day can be created, on the period of the
          // organization's own dedicated day.
          const newDays = shared
            ? roomSelectedDays
                .filter(
                  (selectedDay) =>
                    dedicatedDayOf(selectedDay.day) &&
                    !org.allotmentDays.some(
                      (ad) => ad.day === selectedDay.day && ad.shared,
                    ),
                )
                .map((selectedDay) => ({
                  day: selectedDay.day,
                  periodId: dedicatedDayOf(selectedDay.day).periodId,
                }))
            : [];

          newOrg.allotmentDays = [
            ...newOrg.allotmentDays.map((day) => {
              if (
                day.shared === shared &&
                selectedDays.some(
                  (selectedDay) =>
                    selectedDay.day === day.day &&
                    selectedDay.roomId === room.id,
                )
              ) {
                return {
                  ...day,
                  state: state ?? day.state,
                  count: count ?? day.count,
                  edited: true,
                };
              }

              return day;
            }),
            ...newDays.map((day) => ({
              ...day,
              // New days are shared only, and the shared tab has no status
              // selector (`state` is always null there). Mirror the API's
              // deriveState() for a shared pool: ALLOTMENT when it holds rooms,
              // ON_REQUEST otherwise. Stop/free sale are set on dedicated days.
              state:
                state ??
                (count > 0
                  ? AllotmentDayStateEnum.ALLOTMENT
                  : AllotmentDayStateEnum.ON_REQUEST),
              count: count ?? 0,
              shared: true,
              edited: true,
            })),
          ]?.sort((a, b) => a.day.localeCompare(b.day));

          return newOrg;
        }

        return org;
      });

      return newRoom;
    }

    return room;
  });

  return { rooms: newRooms, skippedDays };
}

export function formatHotelResult(hotel) {
  return {
    id: decodeId(hotel.id),
    // The list sends imageUrl, a single hotel its primaryAttachment.
    image: hotel?.imageUrl ?? hotel?.primaryAttachment?.imageUrl,
    name: hotel?.displayName,
    description: hotel?.address,
    stars: hotel?.stars,
    hasActiveJob: hotel?.hasActiveJob,
  };
}
